/**
 * <dispatch-countdown>
 *
 * Counts down to an absolute dispatch cut-off supplied by the server.
 *
 * The deadline is rendered by Liquid as a UTC epoch timestamp derived from the
 * *store's* timezone, so the value is identical for every visitor regardless of
 * where they are or what their device clock is set to. The element only formats
 * and ticks — it never decides when the cut-off is.
 *
 * Because this is a custom element rather than an inline <script>, it upgrades
 * automatically whenever the markup is re-inserted (Section Rendering API,
 * theme editor section reloads), and disconnectedCallback guarantees the
 * interval is torn down instead of accumulating.
 *
 * Expected markup:
 *   <dispatch-countdown data-deadline="1753286400">
 *     <span data-countdown-output>--:--:--</span>
 *   </dispatch-countdown>
 */
class DispatchCountdown extends HTMLElement {
  connectedCallback() {
    this.output = this.querySelector('[data-countdown-output]');
    if (!this.output) return;

    const absolute = parseInt(this.dataset.deadline, 10);
    const relative = parseInt(this.dataset.secondsRemaining, 10);

    if (Number.isFinite(absolute) && absolute > 1000000000) {
      // Preferred: an absolute epoch is immune to CDN full-page caching, so a
      // cached render still counts down to the correct moment.
      this.deadline = absolute;
    } else if (Number.isFinite(relative) && relative > 0) {
      // Fallback: a plain duration, anchored when the page loads.
      this.deadline = Math.round(Date.now() / 1000) + relative;
    } else {
      this.hidden = true;
      return;
    }

    this.update();
    this.timer = setInterval(this.update.bind(this), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  update() {
    const remaining = this.deadline - Math.round(Date.now() / 1000);

    if (remaining <= 0) {
      clearInterval(this.timer);
      this.hidden = true;
      return;
    }

    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;

    this.output.textContent = [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
  }
}

if (!customElements.get('dispatch-countdown')) {
  customElements.define('dispatch-countdown', DispatchCountdown);
}
